import React, { useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from 'styled-components/native'
import Typography from 'APP/Converse/Typography'
import Icon from 'APP/Converse/Icon'
import Analytics from 'APP/Services/Analytics'
import { CollapsibleContainer, CollapsibleHeader, CollapsibleContent } from './styles'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import { useMarkdownStyles } from 'APP/Hooks/useMarkdownStyles'

export type CollapsibleProps = React.PropsWithChildren<{
  title: string
  onExpand?: () => void
  onCollapse?: () => void
  analyticsName?: string
  testID?: string
}>

const buildButtonValue = (namespace: string | undefined, key: string) =>
  [namespace, key].filter(Boolean).join('.')

export const Collapsible: React.FC<CollapsibleProps> = ({
  title,
  children,
  analyticsName,
  onExpand,
  onCollapse,
  testID,
}) => {
  const theme = useTheme()
  const markdownStyles = useMarkdownStyles({ variant: 'bodyNormal' })
  const [isCollapsed, setIsCollapsed] = useState(true)

  const toggleCollapse = () => {
    if (isCollapsed) {
      Analytics.trackEvent('button_click', {
        button_value: buildButtonValue(analyticsName, 'collapsible_expanded'),
      })
      onExpand?.()
    } else {
      Analytics.trackEvent('button_click', {
        button_value: buildButtonValue(analyticsName, 'collapsible_collapsed'),
      })
      onCollapse?.()
    }
    setIsCollapsed(!isCollapsed)
  }

  return (
    <CollapsibleContainer>
      <TouchableOpacity
        testID={testID}
        onPress={toggleCollapse}
        accessibilityRole="button"
        accessibilityLabel={typeof title === 'string' ? title : undefined}
        accessibilityState={{ expanded: !isCollapsed }}
      >
        <CollapsibleHeader>
          <Typography variant="bodyNormalBold">{title}</Typography>

          <View accessible={false} importantForAccessibility="no-hide-descendants">
            <Icon
              variant="materialIcons"
              src={isCollapsed ? 'keyboard-arrow-down' : 'keyboard-arrow-up'}
              size={theme.metrics.icon.xsmall}
              color="accent"
            />
          </View>
        </CollapsibleHeader>
      </TouchableOpacity>

      <CollapsibleContent collapsed={isCollapsed}>
        {typeof children === 'string' ? (
          <DialogueMarkdown markdown={children} styles={markdownStyles} containerStyles={{}} />
        ) : (
          children
        )}
      </CollapsibleContent>
    </CollapsibleContainer>
  )
}
